
import { computed, defineComponent, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { countriesPhonesCodes } from '@/configs/countries';
import { UserState } from '@/composables/User';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import {
  CountryAndStateConfig,
  CountryAndStateValues,
} from '@/types/country-state-types';
import { normalizeRcsTestDevicePhoneNumber } from '@/types/rcs-onboarding';
import type { RcsDedicatedFormState } from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'RcsTestNumbersStep',

  components: {
    CountryDropdown,
    InputText,
  },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const testNumbers = computed(() => props.form.test_numbers);
    const countryAndState = ref<CountryAndStateValues>({
      country: (UserState.user.country ?? 'FR').toUpperCase() as CountryCode,
      state: '',
    });
    const countryDropdownConfig: CountryAndStateConfig = {
      showStates: false,
      showLabels: false,
      showFlags: true,
      showPhonesCodes: true,
      showOnlyPhoneCodeForSelection: true,
    };

    const normalizeEntry = (index: number): void => {
      const raw = testNumbers.value.numbers[index].number || '';
      const parsed = parsePhoneNumberFromString(
        raw,
        countryAndState.value.country as CountryCode,
      );
      if (parsed?.isValid()) {
        testNumbers.value.numbers[index].number = parsed.number;
        return;
      }
      const localDigits = raw.replace(/\D/g, '');
      if (!localDigits) {
        testNumbers.value.numbers[index].number = '';
        return;
      }
      const dialCode = countriesPhonesCodes[countryAndState.value.country?.toUpperCase()] || '';
      const dialDigits = dialCode.replace(/\D/g, '');
      testNumbers.value.numbers[index].number = dialDigits
        ? `+${dialDigits}${localDigits}`
        : normalizeRcsTestDevicePhoneNumber(raw);
    };

    const getDisplayNumber = (value: string): string => {
      const parsed = parsePhoneNumberFromString(value || '');
      if (parsed?.isValid()) {
        return parsed.nationalNumber;
      }
      return value || '';
    };

    const updateDisplayNumber = (index: number, value: string): void => {
      testNumbers.value.numbers[index].number = value;
    };

    const addNumber = (): void => {
      testNumbers.value.numbers.push({ number: '' });
    };

    const removeNumber = (index: number): void => {
      testNumbers.value.numbers.splice(index, 1);
    };

    return {
      t,
      testNumbers,
      countryAndState,
      countryDropdownConfig,
      getDisplayNumber,
      updateDisplayNumber,
      normalizeEntry,
      addNumber,
      removeNumber,
    };
  },
});
